import { useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { t } from '../i18n/translations'

// Bump the version suffix to re-prompt all users after a material terms change.
const CONSENT_KEY = 'teleshop_terms_accepted_v1'

/**
 * TermsConsent - one-time blocking prompt shown on app entry until the user
 * accepts the Terms of Service and Privacy Policy. The choice is persisted in
 * localStorage, so it appears only once per user (per terms version).
 *
 * While the user is reading a legal page (/page/...), the overlay steps aside so
 * the content is readable; it reappears when they return to the app.
 */
export default function TermsConsent() {
  const location = useLocation()
  const navigate = useNavigate()
  const [accepted, setAccepted] = useState(
    () => localStorage.getItem(CONSENT_KEY) === '1'
  )

  if (accepted) return null
  // Let the user read the legal pages without the blocking overlay.
  if (location.pathname.startsWith('/page/')) return null

  const accept = () => {
    localStorage.setItem(CONSENT_KEY, '1')
    setAccepted(true)
    try {
      window.Telegram?.WebApp?.HapticFeedback?.notificationOccurred('success')
    } catch {
      // ignore
    }
  }

  return (
    <div className="fixed inset-0 z-[1000] flex items-end sm:items-center justify-center bg-black/50 backdrop-blur-sm p-4">
      <div className="w-full max-w-md rounded-2xl bg-white p-6 shadow-xl">
        <h2 className="text-xl font-bold text-gray-900 mb-2 text-center">
          {t('consent.title')}
        </h2>
        <p className="text-sm text-gray-600 text-center mb-4">
          {t('consent.body')}
        </p>
        <div className="flex items-center justify-center gap-2 text-sm mb-5">
          <button
            onClick={() => navigate('/page/terms-of-service')}
            className="text-blue-600 font-medium hover:underline"
          >
            {t('legal.termsOfService')}
          </button>
          <span className="text-gray-300">·</span>
          <button
            onClick={() => navigate('/page/privacy-policy')}
            className="text-blue-600 font-medium hover:underline"
          >
            {t('legal.privacyPolicy')}
          </button>
        </div>
        <button
          onClick={accept}
          className="w-full bg-blue-500 text-white font-semibold py-3 rounded-xl hover:bg-blue-600 active:scale-[0.99] transition"
        >
          {t('consent.accept')}
        </button>
      </div>
    </div>
  )
}
